import { useState, useEffect, useMemo } from 'react';
import api from './services/api';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ptBR } from '@mui/material/locale';
import { createTheme, ThemeProvider, useMediaQuery } from '@mui/material';
import ColorModeContext from './layout/themeContext';
import { ActiveMenuProvider } from './context/ActiveMenuContext/ActiveMenuContext.jsx';
import Favicon from 'react-favicon';
import Routes from './routes';
import defaultLogoFavicon from './assets/favicon.ico';
import defaultLogoFaviconDark from './assets/faviconDark.ico';
import useSettings from './hooks/useSettings';

const queryClient = new QueryClient();

const App = () => {
  const appColorLocalStorage =
    localStorage.getItem('primaryColorLight') ||
    localStorage.getItem('primaryColorDark') ||
    '#065183';

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const preferredTheme = window.localStorage.getItem('preferredTheme');

  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? 'dark' : 'light',
  );

  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);

  const { getPublicSetting } = useSettings();

  const locale = useMemo(() => {
    const i18nlocale = localStorage.getItem('i18nextLng');
    if (!i18nlocale) return undefined;

    const browserLocale =
      i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    return browserLocale === 'ptBR' ? ptBR : undefined;
  }, []);

  const appLogoFavicon = useMemo(() => {
    return mode === 'dark' ? defaultLogoFaviconDark : defaultLogoFavicon;
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          window.localStorage.setItem('preferredTheme', newMode);
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      mode,
    }),
    [mode, setPrimaryColorLight, setPrimaryColorDark],
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          palette: {
            mode,
            primary: {
              main: mode === 'light' ? primaryColorLight : primaryColorDark,
            },
          },

          cssVariables: true,

          colorSchemes: { light: true, dark: true },

          custom: {
            scrollbarStyles: {
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                backgroundColor: mode === 'light' ? '#192734' : '#fff',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: mode === 'light' ? '#999999' : '#99999950',
                borderBottomRightRadius: '4px',
                borderTopRightRadius: '4px',
              },
            },

            scrollbarStylesSoft: {
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbarthumb': {
                backgroundColor: mode === 'light' ? '#F3F3F3' : '#333333',
              },
            },

            cancelButton: {
              color: '#FFF',
              backgroundColor: 'rgb(229, 57, 53)',
              borderColor: mode === 'light' ? 'rgb(126, 2, 0) ' : '#FFF',

              '&:hover': {
                backgroundColor: 'rgba(229, 57, 53, 0.2)',
                color: mode === 'light' ? 'rgb(126, 2, 0) ' : '#FFF',
              },
            },

            editButton: {
              color: '#FFF',
              backgroundColor:
                mode === 'light' ? 'rgb(21, 32, 43)' : 'rgb(29, 161, 242)',
              borderColor: mode === 'light' ? 'rgb(21, 32, 43)' : '#FFF',

              '&:hover': {
                backgroundColor:
                  mode === 'light'
                    ? 'rgba(21, 32, 43, 0.2)'
                    : 'rgba(29, 161, 242, 0.2)',
                color: mode === 'light' ? 'rgb(21, 32, 43)' : '#FFF',
              },
            },

            confirmButton: {
              color: '#FFF',
              backgroundColor: 'rgb(76, 175, 80)',
              borderColor: mode === 'light' ? 'rgb(0, 78, 3)' : '#FFF',

              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: mode === 'light' ? 'rgb(0, 78, 3)' : '#FFF',
              },
            },

            textField: {
              marginRight: 1,
              flex: 1,
              color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,

              '& .MuiInputBase-input': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                WebkitTextFillColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '& .MuiInputLabel-root': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                '&.Mui-focused': {
                  color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                },
                '&.MuiInputLabel-shrink': {
                  color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                },
              },

              '& .MuiInputLabelRoot.Mui-focused': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                  borderWidth: '2px !important',
                },
                '&:hover fieldset': {
                  borderColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                },
                '&.Mui-focused fieldset': {
                  borderColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                },
              },

              '& .MuiIconButton-root': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '& .MuiInputBase-root': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
              },

              '& .MuiSelect-select': {
                color: mode === 'light' ? '#15202B' : '#FFF',
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                borderColor: mode === 'light' ? '#15202B' : '#FFF',
              },

              '& .MuiSelect-icon': {
                color: mode === 'light' ? '#15202B' : '#FFF',
                WebkitTextFillColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '& .MuiMenu-paper': {
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
              },

              '& .MuiMenuItem-root': {
                color: mode === 'light' ? '#15202B' : '#FFF',
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                },
                '&.Mui-selected': {
                  backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                },
              },

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '& .MuiPaper-root': {
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                color: mode === 'light' ? '#15202B' : '#FFF',
                WebkitTextFillColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },

              '& .MuiDialogActions-root .MuiButton-root': {
                WebkitTextFillColor: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
                color: mode === 'light' ? '#15202B' : '#FFF',
              },

              '& .Mui-disabled': {
                color: `grey !important`,
                WebkitTextFillColor: `grey !important`,
              },
              '& .MuiOutlinedInput-root.Mui-disabled': {
                '& fieldset': {
                  borderColor: `grey !important`,
                  opacity: 0.5,
                },
              },
              '& .MuiInputLabel-root.Mui-disabled': {
                color: `grey !important`,
                WebkitTextFillColor: `grey !important`,
                opacity: 0.5,
              },
              '& .MuiIconButton-root.Mui-disabled': {
                color: `grey !important`,
                WebkitTextFillColor: `grey !important`,
                opacity: 0.5,
              },

              '& input[type="time"]::-webkit-calendar-picker-indicator': {
                filter: mode === 'light' ? 'invert(1)' : 'invert(0)',
              },
            },

            selectField: {
              color: mode === 'light' ? '#15202B' : '#FFF',
              backgroundColor: mode === 'light' ? '#EEE' : '#192734',

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'light' ? '#15202B' : '#FFF',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'light' ? '#15202B' : '#FFF',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'light' ? '#15202B' : '#FFF',
              },
              '& .MuiSelect-icon': {
                color: mode === 'light' ? '#15202B' : '#FFF',
              },
              '& .MuiSelect-select': {
                color: mode === 'light' ? '#15202B' : '#FFF',
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
              },

              '& .MuiMenu-paper': {
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
              },

              '& .MuiMenuItem-root': {
                backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                color: mode === 'light' ? '#15202B' : '#FFF',
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                },
                '&.Mui-selected': {
                  backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                },
              },
            },

            label: {
              color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              '&.Mui-focused': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },
              '&.MuiInputLabel-shrink': {
                color: `${mode === 'light' ? '#15202B' : '#FFF'} !important`,
              },
            },

            menuProps: {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getcontentanchorel: null,
              PaperProps: {
                sx: {
                  backgroundColor: mode === 'light' ? '#EEE' : '#192734',
                  color: mode === 'light' ? '#15202B' : '#FFF',
                },
              },
            },

            dialogColor: {
              backgroundColor: mode === 'light' ? '#EEE' : '#192734',
              color: mode === 'light' ? '#15202B' : '#FFF',

              borderTopColor: mode === 'light' ? '#15202B' : '#FFF',
              borderBottomColor: mode === 'light' ? '#15202B' : '#FFF',
            },

            switch: {
              marginLeft: 1,

              '& .MuiSwitch-switchBase.Mui-checked': {
                color: mode === 'light' ? '#294056' : 'rgb(29, 161, 242)',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor:
                  mode === 'light' ? '#294056' : 'rgb(29, 161, 242)',
              },
              '& .MuiSwitch-track': {
                backgroundColor: mode === 'light' ? '#666666' : '#bdbdbd',
              },
            },

            colors: {
              textPrimary:
                mode === 'light' ? primaryColorLight : primaryColorDark,
              borderPrimary:
                mode === 'light' ? primaryColorLight : primaryColorDark,
              dark: mode === 'light' ? '#333333' : '#F3F3F3',
              light: mode === 'light' ? '#F3F3F3' : '#15202B',
              fontColor:
                mode === 'light' ? primaryColorLight : primaryColorDark,
              tabHeaderBackground: mode === 'light' ? '#EEE' : '#192734',
              optionsBackground: mode === 'light' ? '#fff' : '#15202B',
              fancyBackground: mode === 'light' ? '#fafafa' : '#192734',
              total: mode === 'light' ? '#fff' : '#192734',
              messageIcons: mode === 'light' ? 'grey' : '#F3F3F3',
              inputBackground: mode === 'light' ? '#FFFFFF' : '#8899A6',
              barraSuperior: mode === 'light' ? primaryColorLight : '#15202B',
              confirmButton: 'rgb(76, 175, 80)',
              cancelButton: 'rgb(229, 57, 53)',
              editButton:
                mode === 'light' ? 'rgb(21, 32, 43)' : 'rgb(29, 161, 242)',
              warningButton: '#FF9800',
              disabledButton: '#9e9e9e',
              backgroundMessage: mode === 'light' ? '#EEE' : '#192734',
              defaultBackground: mode === 'light' ? '#EEE' : '#192734',
              momentsHeader: '#294056',
              text: mode === 'light' ? '#15202B' : '#FFF',
              hover: mode === 'light' ? '#bdbdbd' : '#666666',
              switchActive: mode === 'light' ? '#294056' : 'rgb(29, 161, 242)',
              switchInactive: mode === 'light' ? '#666666' : '#bdbdbd',
            },
          },
        },
        locale,
      ),
    [locale, mode, primaryColorLight, primaryColorDark],
  );

  useEffect(() => {
    window.localStorage.setItem('preferredTheme', mode);
  }, [mode]);

  useEffect(() => {
    let isMounted = true;

    const loadColors = async () => {
      try {
        const [colorLight, colorDark] = await Promise.all([
          getPublicSetting('primaryColorLight'),
          getPublicSetting('primaryColorDark'),
        ]);

        if (isMounted) {
          if (colorLight) setPrimaryColorLight(colorLight);
          if (colorDark) setPrimaryColorDark(colorDark);
        }
      } catch (error) {
        console.error('Erro ao carregar cores:', error);
        if (isMounted) {
          setPrimaryColorLight('#15202B');
          setPrimaryColorDark('#1DA1F2');
        }
      }
    };

    loadColors();

    return () => {
      isMounted = false;
    };
  }, [getPublicSetting]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--primaryColor',
      mode === 'light' ? primaryColorLight : primaryColorDark,
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get('/version');
        window.localStorage.setItem('frontendVersion', response.data.version);
      } catch (error) {
        console.error(error);
      }
    }
    fetchVersionData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        window.finishProgress &&
        typeof window.finishProgress === 'function'
      ) {
        window.finishProgress();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  return (
    <ColorModeContext.Provider value={{ colorMode }}>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <ActiveMenuProvider>
            <Favicon url={appLogoFavicon} />
            <Routes />
          </ActiveMenuProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
